import { ITeamMember, ROLE_INFO } from '@/@types/TeamMember';
import Badge from '@/components/shared/Badge/Badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { TeamService } from '@/services/team.service';
import { toast } from '@/hooks/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { pt } from 'date-fns/locale';
import { Edit, Link2, Mail, MoreVertical, Shield, Trash2, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface MemberCardProps {
  member: ITeamMember;
  onEdit: (member: ITeamMember) => void;
  onRemove: (member: ITeamMember) => void;
  readOnly?: boolean;
}

export function MemberCard({ member, onEdit, onRemove, readOnly = false }: MemberCardProps) {
  const { t, i18n } = useTranslation('team');
  const queryClient = useQueryClient();
  const roleInfo = ROLE_INFO[member.role];

  const { mutateAsync: resendInvite, isPending: isResending } = useMutation({
    mutationFn: () => TeamService.resendInvite(member.latest_invite_id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast({ description: t('invite_resent') });
    },
  });

  const { mutateAsync: cancelInvite, isPending: isRevoking } = useMutation({
    mutationFn: () => TeamService.cancelInvite(member.latest_invite_id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast({ description: t('invite_revoked') });
    },
  });

  const copyInviteLink = async () => {
    try {
      const inviteUrl = await TeamService.resendInvite(member.latest_invite_id!);
      if (inviteUrl) {
        await navigator.clipboard.writeText(inviteUrl);
        toast({ description: t('link_copied') });
      } else {
        toast({ variant: 'error', description: t('link_copy_error') });
      }
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
    } catch {
      toast({ variant: 'error', description: t('link_copy_error') });
    }
  };

  const getTimeAgo = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const locale = i18n.language?.startsWith('pt') ? pt : undefined;
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale });
  };

  return (
    <div className="p-4 border border-colorBorder rounded-lg bg-card hover:bg-elevated/30 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-full bg-elevated flex-shrink-0 flex items-center justify-center text-sm font-medium text-muted">
            {(member.name || member.email).charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">
              {member.name || member.email}
            </p>
            <p className="text-xs text-muted truncate">{member.email}</p>
          </div>
        </div>

        {!member.is_primary && !readOnly && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(member)}>
                <Edit className="w-4 h-4 mr-2" />
                {t('edit_permissions')}
              </DropdownMenuItem>
              {member.status === 'pending' && member.latest_invite_id && (
                <>
                  <DropdownMenuItem onClick={copyInviteLink}>
                    <Link2 className="w-4 h-4 mr-2" />
                    {t('copy_invite_link')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => resendInvite()}
                    disabled={isResending}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {t('resend_invite')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => cancelInvite()}
                    disabled={isRevoking}
                  >
                    <UserX className="w-4 h-4 mr-2" />
                    {t('revoke_invite')}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem
                onClick={() => onRemove(member)}
                className="text-red-400 focus:text-red-400"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('remove')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <div
          className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
          style={{
            backgroundColor: `${roleInfo.color}1A`,
            color: roleInfo.color,
          }}
        >
          <Shield className="w-3 h-3" />
          {t(`roles.${member.role}`)}
        </div>
        <Badge status={member.status} />
      </div>

      {member.last_access_at && (
        <p className="text-xs text-muted mt-2">
          {t('last_access')}: {getTimeAgo(member.last_access_at)}
        </p>
      )}
    </div>
  );
}
